import classNames from 'classnames';
import React, { useRef } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import FloatingActionsDropdown, {
  FloatingActionsDropdownRef,
} from '@/components/molecules/floating-actions-dropdown/floating-actions-dropdown';
import ProfilePicture from '@/components/molecules/profile-picture/profile-picture';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './individual-traveler-card.module.scss';

export interface IndividualTravelerCardItem {
  /** The first name to show */
  firstName?: Content.Components['TripPassengerDto']['firstName'];
  /** The profile picture url */
  imageUrl?: Content.Components['TripPassengerDto']['imageUrl'];
  /** Whether the viewing user matches the traveler */
  isCurrentUser?: Content.Components['TripPassengerDto']['isCurrentUser'];
  /** The last name to show */
  lastName?: Content.Components['TripPassengerDto']['lastName'];
  /** An event that is triggered on click reactivate invitation */
  onClickReactivateInvitation?: () => void;
  /** An event that is triggered on click revoke invitation */
  onClickRevokeInvitation?: () => void;
  /** The invitation status for the passenger */
  status?: Content.Components['TripPassengerDto']['status'];
  /** The unique id of the traveler */
  uniquePaxHash: Content.Components['TripPassengerDto']['uniquePaxHash'];
}

interface ActionItem {
  description: string;
  label: string;
  onClick: () => void;
  type: 'reactivate' | 'revoke';
}

export interface IndividualTravelerCardProps {
  /** The class name to apply */
  className?: string;
  /** The unique id of the modal */
  modalId?: string;
  /** The traveler item to show */
  traveler: IndividualTravelerCardItem;
  /** The role of the user */
  viewerMode?: 'admin' | 'guest';
}

const IndividualTravelerCard = ({
  className,
  modalId,
  traveler,
  viewerMode,
}: IndividualTravelerCardProps) => {
  const { t } = useI18n();
  const dropdownRef = useRef<FloatingActionsDropdownRef>(null);
  const {
    firstName,
    imageUrl,
    isCurrentUser,
    lastName,
    onClickReactivateInvitation,
    onClickRevokeInvitation,
    status,
  } = traveler;

  const hasAdminRights = viewerMode === 'admin';
  const hasGuestRights = viewerMode === 'guest';
  const fullName = `${firstName} ${lastName}`;

  const actionItems: ActionItem[] = [];

  if (status === 'invited' || status === 'accepted') {
    actionItems.push({
      label: t('items.profile.profilePage.coTravelers.removeFromTripHeadline'),
      description: t(
        'items.profile.profilePage.coTravelers.removeFromTripDescription',
      ),
      onClick: () => onClickRevokeInvitation?.(),
      type: 'revoke',
    });
  }

  if (status === 'notInvited') {
    actionItems.push({
      label: t('items.profile.profilePage.coTravelers.addToTripHeadline'),
      description: t(
        'items.profile.profilePage.coTravelers.addToTripDescription',
      ),
      onClick: () => onClickReactivateInvitation?.(),
      type: 'reactivate',
    });
  }

  const renderInvitationLinkAdminActions = () => {
    const dropdownTrigger = (
      <div className={styles.triggerButton}>
        <Icon className={styles.overflowIcon} name="Overflow" size={24} />
      </div>
    );

    const dropdownContent = (
      <div className={styles.actionItems}>
        {actionItems.map((actionItem, actionItemIndex) => (
          <Link
            key={actionItemIndex}
            className={styles.actionItem}
            onClick={() => {
              actionItem.onClick();
              dropdownRef.current?.close();
            }}
          >
            <Typography
              className={classNames(styles.actionItemLabel, {
                [styles.isReactivate]: actionItem.type === 'reactivate',
                [styles.isRevoke]: actionItem.type === 'revoke',
              })}
              tagStyle="bodyMediumBold"
            >
              {actionItem.label}
            </Typography>

            <Typography
              className={styles.actionItemDescription}
              tagStyle="bodySmall"
            >
              {actionItem.description}
            </Typography>
          </Link>
        ))}
      </div>
    );

    return (
      <FloatingActionsDropdown
        ref={dropdownRef}
        alignment="right"
        content={dropdownContent}
        modalId={modalId}
        trigger={dropdownTrigger}
      />
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasAdminRights]: hasAdminRights,
      })}
    >
      <div className={styles.content}>
        <ProfilePicture
          firstName={firstName}
          imageUrl={imageUrl ?? undefined}
          initialsTagStyle="labelLarge"
          lastName={lastName}
          mode="invitation"
          size={48}
        />

        <div className={styles.details}>
          {hasGuestRights && isCurrentUser && (
            <div className={styles.currentUserWrapper}>
              <Icon name="User" size={16} />

              <Typography className={styles.fullName} tagStyle="bodySmall">
                {t('items.profile.profilePage.coTravelers.me')}
              </Typography>
            </div>
          )}

          <Typography className={styles.fullName} tagStyle="bodyMediumBold">
            {fullName}
          </Typography>

          {hasAdminRights && status === 'accepted' && (
            <div className={styles.statusWrapper}>
              <Typography className={styles.statusText} tagStyle="bodySmall">
                {`${t('items.profile.profilePage.coTravelers.status')}:`}
              </Typography>

              <div className={styles.statusValue}>
                <Typography tagStyle="bodySmall">
                  {t('items.profile.profilePage.coTravelers.accepted')}
                </Typography>

                <div className={styles.iconContainer}>
                  <Icon name="CheckMark" size={8} />
                </div>
              </div>
            </div>
          )}
        </div>

        {hasAdminRights && actionItems.length > 0 && (
          <div className={styles.adminActions}>
            {renderInvitationLinkAdminActions()}
          </div>
        )}
      </div>
    </div>
  );
};

export default IndividualTravelerCard;
